import { IoCContainer, WebCoreIoCKeys } from '../../..';
import { WebCoreSettings } from '../../../ioc/implentation/web-core-settings/web-core-settings.class';
import { SidebarSide } from '../../../pocos/sidebar.class';
import { SidebarComponent } from '../sidebar-component.class';

export class DragHandler {
    private settings: WebCoreSettings;
    public isDragging = false;
    public isDraggingModule = false;

    get positions(): string[] {
        let allPositions = Object.values(SidebarSide) as SidebarSide[];
        let occupiedSides = this.settings.sidebarManager.sidebarList.map(item => item.orientation);
        let availablePositions = allPositions.filter(pos => !occupiedSides.includes(pos));
        return availablePositions;
    }

    constructor(
        private sidebarRef: SidebarComponent,
        private index: number
    ) {
        this.settings = IoCContainer.get(WebCoreIoCKeys.IWebCoreSettings);
    }

    public initModuleWrapperDraggable(): void {
        let moduleWrapperList = Array.from(this.sidebarRef.shadow.querySelectorAll('.wecdev-module-wrapper')) as HTMLElement[];
        let sidebar = this.sidebarRef.getSidebarNode();

        if (!sidebar) return;

        let draggedModule: HTMLElement | null = null;
        let moduleName: string;

        for (let wrapperElement of Array.from(moduleWrapperList)) {
            let wrapper = wrapperElement as HTMLFieldSetElement;
            wrapper.setAttribute('draggable', 'true');

            wrapper.addEventListener('dragstart', event => {
                this.isDraggingModule = true;
                draggedModule = wrapper;
                event.dataTransfer!.setData('text/plain', '');
            });

            wrapper.addEventListener('dragend', () => {
                this.isDraggingModule = false;
                draggedModule = null;
            });
        }

        sidebar.addEventListener('dragover', event => {
            if (!this.isDraggingModule) return;
            event.preventDefault();
        });

        sidebar.addEventListener('drop', event => {
            if (!draggedModule) return;
            event.preventDefault();

            let sidebarRect = sidebar.getBoundingClientRect();
            let x = event.clientX - sidebarRect.left;
            let y = event.clientY - sidebarRect.top;

            draggedModule.style.left = `${x}px`;
            draggedModule.style.top = `${y}px`;

            let legend = draggedModule.querySelector('.wecdev-legend');
            if (legend) {
                moduleName = legend.textContent?.trim() || '';
            } else {
                moduleName = '';
            }

            this.sidebarRef.setModulePosition(moduleName, x, y);
        });
    }

    public initDocking(): void {
        let sidebar = this.sidebarRef.getSidebarNode();
        if (!sidebar) return;

        let overlay = document.getElementById(`dock-overlay-${this.index}`) as HTMLElement;
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = `dock-overlay-${this.index}`;
            overlay.style.cssText = `
                display: none;
                position: fixed;
                inset: 0;
                background: rgba(0,0,0,0.3);
                z-index: 9999;
            `;
            document.body.appendChild(overlay);
        }

        sidebar.setAttribute('draggable', 'true');

        sidebar.addEventListener('dragstart', (event: DragEvent) => {
            if (event.target !== sidebar) return;

            if (!this.sidebarRef.isPanelOpen) {
                event.preventDefault();
                return;
            }

            this.isDragging = true;
            this.refreshDropZones(overlay);
            overlay.style.display = 'block';
        });

        sidebar.addEventListener('dragend', (event: DragEvent) => {
            this.isDragging = false;
            overlay.style.display = 'none';
        });
    }

    private refreshDropZones(overlay: HTMLElement): void {
        overlay.innerHTML = '';

        let availablePositions = this.positions;
        availablePositions.forEach(pos => {
            let zone = document.createElement('div');
            zone.className = `drop-zone ${pos}`;
            zone.style.position = 'absolute';
            zone.style.background = 'rgba(255,255,255,0.2)';
            zone.style.border = '2px dashed #999';
            zone.style.width = '75px';
            zone.style.height = '75px';
            zone.style.transition = 'background 0.2s';

            switch (pos) {
                case 'top':
                    zone.style.top = '10px';
                    zone.style.left = '50%';
                    zone.style.transform = 'translateX(-50%)';
                    break;
                case 'bottom':
                    zone.style.bottom = '10px';
                    zone.style.left = '50%';
                    zone.style.transform = 'translateX(-50%)';
                    break;
                case 'left':
                    zone.style.left = '10px';
                    zone.style.top = '50%';
                    zone.style.transform = 'translateY(-50%)';
                    break;
                case 'right':
                    zone.style.right = '10px';
                    zone.style.top = '50%';
                    zone.style.transform = 'translateY(-50%)';
                    break;
            }

            overlay.appendChild(zone);

            zone.addEventListener('dragover', e => {
                e.preventDefault();
                zone.style.background = 'rgba(255,255,255,0.5)';
            });

            zone.addEventListener('dragleave', () => {
                zone.style.background = 'rgba(255,255,255,0.2)';
            });

            zone.addEventListener('drop', () => {
                overlay.style.display = 'none';

                const sidebar = this.sidebarRef.getSidebarNode();
                if (!sidebar) return;

                sidebar.classList.remove('left', 'right', 'top', 'bottom');

                if (zone.classList.contains('top')) {
                    this.sidebarRef.currentSide = SidebarSide.Top;
                }
                if (zone.classList.contains('bottom')) {
                    this.sidebarRef.currentSide = SidebarSide.Bottom;
                }
                if (zone.classList.contains('left')) {
                    this.sidebarRef.currentSide = SidebarSide.Left;
                }
                if (zone.classList.contains('right')) {
                    this.sidebarRef.currentSide = SidebarSide.Right;
                }

                this.sidebarRef.getSidebarNode().dispatchEvent(
                    new CustomEvent('dock:changed', {
                        detail: this.sidebarRef.currentSide,
                    })
                );

                this.sidebarRef.updateResizable();
            });
        });
    }
}
